import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { PwaConfigService } from '../../pwa/services/pwa-config.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { EventService } from '../../shared/services/event.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { GuidGenerator } from '../../shared/utilities/guid-generator.utility';
import { UploadSource } from '../models/upload-session.model';
import { UploadButtonService } from '../upload-button/upload-button.service';

export type UploadFileObject = {
    filePath: string;
    fileName: string;
    type: UploadActionType;
};

export const enum UploadActionType {
    Pin = 0,
    Open = 1,
    Share = 2,
    Favorite = 3
}

export const enum FileSystemAccessPoint {
    AllowFileSystemAccessUpload = 'allow-file-system-access-upload',
    AllowFilesSystemAccessOpenFileLocation = 'allow-file-system-access-open-file-location'
}

@Injectable({
    providedIn: 'root'
})
export class UploadLocalFileService {
    private readonly _modalInfo: BehaviorSubject<UploadFileObject>;
    private currentUploadSessionId: string;
    private nextUploadSessionId: string;

    constructor(
        private readonly eventService: EventService,
        private readonly pwaConfigService: PwaConfigService,
        private readonly uploadButtonService: UploadButtonService,
        private readonly instrumentationService: InstrumentationService
    ) {
        this._modalInfo = new BehaviorSubject<UploadFileObject>(null);
        this.updateNextSessionId();
    }

    public showModal(path: string, name: string, actionType: UploadActionType) {
        this._modalInfo.next({filePath: path, fileName: name, type: actionType});
    }

    public get modalInfo(): Observable<UploadFileObject> {
        return this._modalInfo.asObservable();
    }

    public uploadFile(file: File, elementId: string) {
        this.currentUploadSessionId = this.nextUploadSessionId;
        this.updateNextSessionId();
        this.uploadButtonService.uploadFile(file, null, this.currentUploadSessionId, elementId, UploadSource.Button);
    }

    public getLocalFileInfo(filePath: string, name: string): Promise<File> {
        return new Promise<File>(resolve => {

            // TODO: Mock out Windows file system apis to test error scenarios
            if (this.pwaConfigService.config.mock) {
                resolve(new File([], name));
                return;
            }

            Windows.Storage.StorageFile.getFileFromPathAsync(filePath)
                .then(storageFile => {
                    if (!storageFile) {
                        throw new Error('Null file returned for path');
                    }
                    return Windows.Storage.FileIO.readBufferAsync(storageFile).then(buffer => buffer, () => {
                        throw new Error('Unable to create buffer');
                    });
                }, () => {
                    this.eventService.broadcastEvent({
                        name: AngularEvents.needAccessToFileSystem,
                        args: [FileSystemAccessPoint.AllowFileSystemAccessUpload]
                    });
                    throw new Error('Unable to access local file');
                }).then(buffer => {
                    if (buffer) {
                        try {
                            const reader = Windows.Storage.Streams.DataReader.fromBuffer(buffer);
                            const size = reader.unconsumedBufferLength;
                            const fileBytes = new Uint8Array(size);
                            for (let b = 0; b < size; b++) {
                                fileBytes[b] = reader.readByte();
                            }
                            const blob: any = new Blob([fileBytes]);
                            blob.lastModifiedDate = new Date();
                            blob.name = name;
                            resolve(blob as File);
                        } catch (e) {
                            throw new Error('Error reading from buffer');
                        }
                    } else {
                        throw new Error('Null buffer');
                    }
                }).then(null, (error: Error) => {
                    const params: InstrumentationParams = {};
                    params[InstrumentationParam.Error] = error ? error.message : '';
                    params[InstrumentationParam.ItemCategory] = UploadSource[UploadSource.Button];
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UploadLocalError, params, [], DataSecurityLevel.Enhanced);
                });
        });
    }

    private updateNextSessionId() {
        this.nextUploadSessionId = GuidGenerator.createGuid();
    }
}
